import { Box, ContextMenu, Flex, Table, Text } from '@radix-ui/themes';
import { ClickCatcher } from 'components/common/layout/click-catcher';
import { SEPARATOR_LABEL, setupMenuActions } from 'components/common/menu';
import { convertTableToMenu } from 'components/common/table/actions-cells';
import { TableBodyLoading } from 'components/common/table/body-loading';
import { TableContext } from 'components/common/table/context';
import { CommonTableHeaderCell } from 'components/common/table/header-cell';
import { TableInfiniteLoader } from 'components/common/table/infinite-loader';
import { NoDataPlaceholder } from 'components/common/table/no-data-placeholder';
import { TableRow } from 'components/common/table/row';
import { CheckedContext } from 'contexts/layout/checked.context';
import { ACTIONS_KEY } from 'enums/tables';
import { useOnContextMenu } from 'hooks/useOnContextMenu';
import { t } from 'i18next';
import { IMenuAction } from 'interfaces/i-menus';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ICommonTableProps } from 'interfaces/tables/main';
import { StringHelper } from 'lib_ts/classes/string.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { CSSProperties, useContext, useMemo } from 'react';

const ENABLE_CHECKED_ACTIONS = false;
const ENABLE_CONTEXT_MENU = true;

interface IProps extends ICommonTableProps {
  // * this should be a memoized value to avoid unnecessary redraws
  columns: ITypedTableColumn<any>[];
}

export const CommonTableBody = (props: IProps) => {
  const { contextTarget, pageData, sortedData, tableKey, setContextTarget } =
    useContext(TableContext);

  const { tally, getChecked } = useContext(CheckedContext);

  const visibleColumns = useMemo(() => {
    return props.columns.filter((c) => !c.invisible);
  }, [props.columns]);

  // lifted from footer component
  const checkedActions = useMemo(() => {
    if (!ENABLE_CHECKED_ACTIONS) {
      return undefined;
    }

    if (!props.checkedActions) {
      return undefined;
    }

    const items = sortedData.filter((m) => getChecked(m._id));
    if (items.length === 0) {
      // note: actions that are not related to checked items should not be put here!
      return undefined;
    }

    const output = props.checkedActions(items).filter((a) => !a.invisible);
    if (output.length === 0) {
      return undefined;
    }

    output.forEach((o) => {
      o.group = 'common.selected';
    });

    return output;
  }, [
    // faster than monitoring sortedData
    tableKey,
    tally.key,
    props.checkedActions,
  ]);

  const rowActions = useMemo(() => {
    if (!contextTarget) {
      return undefined;
    }

    const actionColumn = props.columns.find((c) => c.key === ACTIONS_KEY);

    if (!actionColumn?.actions) {
      return undefined;
    }

    return actionColumn.actions
      .map((a) => convertTableToMenu(a, contextTarget))
      .filter((a) => !a.invisible);
  }, [contextTarget, props.columns]);

  const contextActions = useMemo(() => {
    const output: IMenuAction[] = [];

    if (rowActions) {
      output.push(...rowActions);
    }

    if (checkedActions) {
      output.push(...checkedActions);
    }

    return setupMenuActions(
      output.filter((m) => !m.invisible),
      true
    );
  }, [rowActions, checkedActions]);

  const { onContextMenuHandlers } = useOnContextMenu({
    longPressEnabled: !!props.enableLongPressContextMenu,
    onContextMenu: () => {
      // console.debug('clear context menu target from table root');
      setContextTarget(undefined);
    },
  });

  const showInfiniteLoader =
    !!props.onLoadMore && props.hasMore && !!pageData.length;

  const tableCss = useMemo(() => {
    const output: CSSProperties = {};

    // scrolling tables draw their frame on the scroll root, so the table border
    // stays off. without a scroll root the table itself keeps the frame from CSS
    if (props.disableScrolling && !props.squareBorder) {
      output.overflow = 'hidden';
    } else if (props.squareBorder) {
      // square border
      output.border =
        '1px solid color-mix(in oklab, var(--gray-a5), var(--gray-5) 25%)';
      output.borderRadius = 0;
    } else {
      // rounded border
      // the frame is drawn on the scroll root (or the table element when scrolling
      // is off). a border here sits inside that frame and reads as a second, darker edge
      output.overflow = 'hidden';
      output.border = 'none';
      output.borderRadius = 0;
    }

    if (props.vFlex) {
      output.position = 'absolute';
      output.top = 0;
      output.bottom = 0;
      output.left = 0;
      output.right = 0;
    }

    if (props.scrollHeight) {
      output.height = props.scrollHeight;
    }

    return output;
  }, [
    props.squareBorder,
    props.vFlex,
    props.scrollHeight,
    props.disableScrolling,
  ]);

  const tableRoot = useMemo(() => {
    if (!props.loading && pageData.length === 0) {
      return (
        <NoDataPlaceholder
          className="rt-TablePlaceholder"
          vFlex={props.vFlex}
          noDataBody={props.noDataBody}
          noDataHeader={props.noDataHeader}
          noDataButton={props.noDataButton}
        />
      );
    }

    return (
      <Table.Root
        className={props.className}
        style={tableCss}
        data-testid={props.dataTestID}
        {...onContextMenuHandlers}
      >
        <Table.Header>
          <Table.Row>
            {visibleColumns.map((col, iCol) => (
              <CommonTableHeaderCell
                key={iCol}
                col={col}
                enableSort={!!props.enableSort}
                checkAllIDs={props.displayData.map((m) => m._id)}
                afterCheckAll={props.afterCheckAll}
              />
            ))}
          </Table.Row>
        </Table.Header>

        {props.loading ? (
          <TableBodyLoading
            rows={6}
            visibleColumns={visibleColumns.length}
            rowClasses={props.rowClassNameFn?.(undefined)}
          />
        ) : (
          <Table.Body>
            {pageData.map((item, index) => (
              <TableRow
                key={`table-${props.dataTestID}-row-${item._id}`}
                tableID={props.dataTestID}
                index={index}
                item={item}
                visibleColumns={visibleColumns}
                {...props}
              />
            ))}

            {showInfiniteLoader && (
              <TableInfiniteLoader colSpan={visibleColumns.length} {...props} />
            )}
          </Table.Body>
        )}

        {!props.loading && props.footerRows && (
          <tfoot>
            {props.footerRows.map((r) => (
              <TableRow
                key={`table-${props.dataTestID}-row-footer-${r._id}`}
                tableID={props.dataTestID}
                item={r}
                visibleColumns={visibleColumns}
                index={-1}
              />
            ))}
          </tfoot>
        )}
      </Table.Root>
    );
  }, [
    pageData,
    props.footerRows,
    tableCss,
    props.loading,
    props.squareBorder,
    showInfiniteLoader,
  ]);

  if (!ENABLE_CONTEXT_MENU) {
    // right-click shouldn't trigger any menu
    return tableRoot;
  }

  return (
    <ClickCatcher>
      <ContextMenu.Root
        onOpenChange={(open) => {
          if (open) {
            // do nothing if the menu just opened
            return;
          }

          // this is for the case where the menu was closed and no new menu was opened, then reset the target so the highlight goes away
          // this should occur before any context event of right-clicking a new row goes through (which would select a new target) because of the timeout
          // this ensures that clearing things here will not interfere with selecting a new target for the new row
          setContextTarget(undefined);
        }}
      >
        <ContextMenu.Trigger>{tableRoot}</ContextMenu.Trigger>

        {contextTarget && contextActions.length > 0 && (
          <ContextMenu.Content
            key={contextTarget._id}
            className="MenuContent scroll-hover"
          >
            {contextActions.map((a, i) =>
              a.label === SEPARATOR_LABEL ? (
                <ContextMenu.Separator
                  key={i}
                  data-group={a.group}
                  className="MenuSeparator"
                  // avoid starting a menu with a separator
                  hidden={i === 0}
                />
              ) : (
                <ContextMenu.Item
                  key={i}
                  className={StringHelper.classNames(['MenuItem', a.className])}
                  data-group={a.group}
                  title={a.tooltip ? t(a.tooltip).toString() : undefined}
                  disabled={a.disabled}
                  onClick={a.onClick}
                >
                  {a.prefixIcon && (
                    <Box
                      className="valign-center"
                      style={{
                        marginRight: '8px',
                      }}
                    >
                      <Text
                        color={a.disabled ? RADIX.COLOR.SECONDARY : a.color}
                      >
                        <Flex align="center">{a.prefixIcon}</Flex>
                      </Text>
                    </Box>
                  )}

                  <Box className="valign-center">
                    <Text
                      color={a.disabled ? RADIX.COLOR.SECONDARY : a.color}
                      title={t(a.label).toString()}
                      truncate
                    >
                      {t(a.label)}
                    </Text>
                  </Box>

                  {a.suffixIcon && (
                    <Box
                      className="valign-center"
                      style={{
                        marginLeft: '8px',
                      }}
                    >
                      <Flex align="center">{a.suffixIcon}</Flex>
                    </Box>
                  )}
                </ContextMenu.Item>
              )
            )}
          </ContextMenu.Content>
        )}
      </ContextMenu.Root>
    </ClickCatcher>
  );
};
